import React, { useRef, useState } from "react";
import { UseFormRegister } from "react-hook-form";
import { fileToBase64 } from "../../lib/utils";
import { FiTrash2 } from "react-icons/fi";
import InfoTooltip from "../../components/common/InfoTooltip";

type PaymentFormInputs = {
  SSN?: string;
  merchantCode?: string;
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  transitNumber: string;
  institutionNumber: string;
  swiftCode: string;
  vendorImageBlob: string;
  vendorImageType: string;
  vendorImage: string;
  DOB: string;
};

type PaymentInformationProps = {
  register: UseFormRegister<PaymentFormInputs>;
  errors: any;
  watch: any;
  setValue: any;
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
};

const PaymentDetails: React.FC<PaymentInformationProps> = ({
  register,
  watch,
  errors,
  setValue,
  setShowAlertOnRouteChange,
}) => {
  return (
    <div className="w-full grid pb-2">
      <div className="col-span-4 space-y-6 w-full flex flex-col">
        <div className="flex flex-col p-4 shadow-sm rounded-lg  bg-white">
          <h1 className="font-helveticaMedium mb-2 text-lg flex items-center">
            Payment & Payout Details (Optional)
            <InfoTooltip
              placement="left"
              content="Set up your payment information to receive payouts from your sales. Your details will be securely stored and used exclusively by Stripe to process your payments."
            />
          </h1>

          <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
            SIN
            <InfoTooltip content="Your Social Insurance Number (SIN) is required for identity verification and tax reporting. This information is securely stored and encrypted." />
          </label>
          <input
            type="text"
            {...register("SSN")}
            className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
            placeholder="Enter or update your Social Secuirty Number for tax purposes"
          />

          <label className="text-sm mt-3 font-helveticaMedium text-customBlackColor flex items-center">
            Merchant Code
            <InfoTooltip content="Enter the last 4 digits of your merchant code provided by your payment processor." />
          </label>
          <input
            type="text"
            {...register("merchantCode")}
            maxLength={4}
            className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
            placeholder="Enter your merchant code last four digits for payment processing"
          />
        </div>

        <div className="flex flex-col p-4 shadow-sm rounded-lg bg-white">
          <h1 className="font-helveticaMedium mb-2 text-lg">Bank Details</h1>
          <label className="text-sm font-helveticaMedium text-customBlackColor">
            Account Holder Name <span className="text-red-600"></span>
          </label>
          <input
            type="text"
            {...register("accountHolderName")}
            className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
            placeholder="Enter the name on account"
          />

          <label className="mt-3 text-sm font-helveticaMedium text-customBlackColor flex items-center">
            Date of Birth
            <InfoTooltip content="Required by Stripe to manage payouts and ensure compliance with financial regulations." />
          </label>
          <input
            type="date"
            {...register("DOB")}
            className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm h-[38px] [&::-webkit-calendar-picker-indicator]:float-left [&::-webkit-datetime-edit]:text-left"
            placeholder="Select date"
            onClick={(e) => e.currentTarget.showPicker()}
          />

          <label className="mt-3 text-sm font-helveticaMedium text-customBlackColor">
            Bank Name <span className="text-red-600"></span>
          </label>
          <input
            type="text"
            {...register("bankName")}
            className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
            placeholder="Enter the name of your bank"
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mt-3 text-sm font-helveticaMedium text-customBlackColor flex items-center">
                Transit Number <span className="text-red-600"></span>
                <InfoTooltip content="The transit number (5 digits) identifies your specific bank branch. You can find this number at the bottom of your checks or by contacting your bank." />
              </label>
              <input
                type="text"
                {...register("transitNumber")}
                maxLength={5}
                className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
                placeholder="Enter your 5-digit transit number"
              />
            </div>

            <div>
              <label className="mt-3 text-sm font-helveticaMedium text-customBlackColor flex items-center">
                Institution Number <span className="text-red-600"></span>
                <InfoTooltip content="The institution number (3 digits) identifies your bank. Contact your bank or check your account details to find this number." />
              </label>
              <input
                type="text"
                {...register("institutionNumber")}
                maxLength={3}
                className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
                placeholder="Enter your 3-digit institution number"
              />
            </div>
          </div>

          <label className="mt-3 text-sm font-helveticaMedium text-customBlackColor">
            Account Number <span className="text-red-600"></span>
          </label>
          <input
            type="text"
            {...register("accountNumber")}
            className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
            placeholder="Enter the account number of your bank"
          />
        </div>
      </div>
    </div>
  );
};
export default PaymentDetails;
